import { isForcedAutopayRecurringSelector, saleSelectors } from 'store/sale/selectors';
import { RootState } from 'store/store';
import { COUNTRY, PAYMENT_DECLINE_REASON_CODES } from 'types/constants';

const { SplunkReporter } = require('reporting/splunk/SplunkReporter');
const splunkReporter = SplunkReporter.getInstance();
const logger = 'store/ixp/selector';
/**
 * IXP Selector module
 */

/**
 * @typedef {import('./selector').Store} reduxStore
 */

const logToSplunk = ({ error }: { error: any }) => {
  splunkReporter.contextual({
    logInfo: { logLevel: 'warn', logger },
    event: 'viewSale',
    action: 'render',
    error: {
      message: error?.message,
      stack: error?.stack,
    },
  });
};

/**
 * Get experiment treatment name
 * @param {reduxStore} store - redux store
 * @param {string} experimentKey
 * @returns {string} treatment payload
 */
export function ixpTreatmentSelector({ ixp = {} }: IXPSelectorState, experimentKey: string) {
  try {
    if (!ixp[experimentKey]) {
      return false;
    }
    const { isRunning, control, treatmentKey } = ixp[experimentKey];
    return isRunning && !control ? treatmentKey : false;
  } catch (error) {
    logToSplunk({ error });
    return false;
  }
}

export function ixpTreatmentPayloadSelector({ ixp }: IXPSelectorState, experimentKey: string) {
  try {
    const { isRunning, treatmentPayload } = ixp[experimentKey];
    return isRunning === true ? JSON.parse(treatmentPayload) : false;
  } catch (error) {
    logToSplunk({ error });
    return false;
  }
}

export function isInIXPExperimentalTreatment(store: IXPSelectorState, experimentKey: string) {
  if (!store.ixp) {
    return false;
  }
  if (!(experimentKey in store.ixp)) {
    // Experiment not found, no need to log
    return false;
  }
  try {
    const { isRunning, control, treatmentKey, treatmentPayload } = store.ixp[experimentKey];
    const isForcedOff = treatmentPayload === 'false' && treatmentKey === 'false';

    return isRunning === true && control === false && !isForcedOff;
  } catch (error) {
    logToSplunk({ error });
    return false;
  }
}

export function isInIXPControlTreatment(store: IXPSelectorState, experimentKey: string) {
  if (!store.ixp) {
    return false;
  }
  if (!(experimentKey in store.ixp)) {
    // Experiment not found, no need to log
    return false;
  }
  try {
    const { isRunning, control } = store.ixp[experimentKey];
    return isRunning === true && control === true;
  } catch (error) {
    logToSplunk({ error });
    return false;
  }
}

export type IXPSelectorState = Pick<RootState, 'ixp' | 'featureFlags'>;

export const getTrustedCheckoutVariation = (store: IXPSelectorState) => {
  try {
    const { featureFlags = {} } = store;
    return (
      featureFlags.isTrustedCheckoutEnabled && ixpTreatmentPayloadSelector(store, 'trustedCheckout')
    );
    // return 'TEMP';
  } catch (error) {
    return null;
  }
};

export const isGrowthNpeFreeTextEmailEnabled = (store: IXPSelectorState) => {
  return (
    store.featureFlags &&
    store.featureFlags['growth-npe-free-text-email'] &&
    isInIXPExperimentalTreatment(store, 'NpeFreeTextEmail')
  );
};

export const isInvoiceLineItemsEnabled = (store: IXPSelectorState) => {
  return (
    store?.featureFlags &&
    store?.featureFlags['cp-invoice-line-items'] &&
    isInIXPExperimentalTreatment(store, 'InvoiceLineItems')
  );
};

export const isGrowthPayPalCheckoutEnabled = (store: IXPSelectorState) => {
  return (
    store?.featureFlags['SBSEG-CP-growth-paypal-checkout'] &&
    ixpTreatmentSelector(store, 'PayPalCheckout')
  );
};

export const isPPAAMPayLaterEnabled = (store: IXPSelectorState) => {
  return (
    store.featureFlags &&
    !store.featureFlags['block-paypal-ppaam-paylater-payments'] &&
    isInIXPExperimentalTreatment(store, 'PPAAMPayLater')
  );
};

export const isRevIntelExperimentEnabled = (store: IXPSelectorState) => {
  return isInIXPExperimentalTreatment(store, 'MailchimpRevIntel');
};

export const isInIXPExperimentalTreatmentSelector =
  (IXP_KEY: string) => (store: IXPSelectorState) => {
    return isInIXPExperimentalTreatment(store, IXP_KEY);
  };

export const isIXPExperimentExistSelector =
  (IXP_KEY: string) =>
  (store: IXPSelectorState): boolean => {
    return IXP_KEY in store.ixp;
  };

export const isPostInvoiceSurveyEnabled = (store: IXPSelectorState) => {
  return store.featureFlags && store.featureFlags['SBSEG-CP-revintel-post-invoice-survey'];
};
/** TODO: Remove once we fully migrate to sku based flag */
export const isRevIntelMs2ExperienceEnabled = (__store: IXPSelectorState) => {
  return true;
};

// global flag gating the following experience updates: post-invoice survey step expansion, testimonials & individual settings management
export const isRevIntelSurveyOptimizationsEnabled = (store: IXPSelectorState) => {
  const featureFlags = store.featureFlags;
  return (
    featureFlags &&
    isRevIntelMs2ExperienceEnabled(store) &&
    featureFlags['gbsg-revintel-survey-optimizations-settings-mgmt']
  );
};

// global flag gating the following experience updates: testimonials & individual settings management
export const isRevIntelSurveyReviewsAndSettingsEnabled = (store: IXPSelectorState) => {
  const featureFlags = store.featureFlags;
  return featureFlags && isRevIntelSurveyOptimizationsEnabled(store);
};

// component feature flag gating survey step expansion
export const shouldAlwaysShowFeedback = (store: IXPSelectorState) => {
  const featureFlags = store.featureFlags;
  return (
    featureFlags &&
    isRevIntelSurveyOptimizationsEnabled(store) &&
    featureFlags['gbsg-revintel-survey-always-show-feedback']
  );
};

export const isFrictionlessLoginPhase3Enabled = (store: IXPSelectorState) => {
  if (!store?.featureFlags?.['cp-frictionless-login-phase-3-enabled']) {
    return false;
  }

  const isInFrictionlessLoginPhase3Experiment =
    !!store?.ixp?.['FrictionlessLoginPhase3'] &&
    !!store?.ixp?.['FrictionlessLoginPhase3']?.isRunning &&
    store?.ixp?.['FrictionlessLoginPhase3']?.treatmentKey === 'IXP2_T_1028656';

  if (!isInFrictionlessLoginPhase3Experiment) {
    return false;
  }

  return true;
};

export const hasGASurveys = (store: IXPSelectorState) => {
  return store?.featureFlags && store?.featureFlags['SBSEG-CP-crm-july-sku-launch'];
};

export const isAiAssistEnabled = (store: IXPSelectorState) => {
  return (
    !!store.featureFlags?.['cp-c2-ai-assist'] &&
    !!store?.ixp?.['C2AIAssist'] &&
    !!store?.ixp?.['C2AIAssist']?.isRunning &&
    store?.ixp?.['C2AIAssist']?.treatmentKey === 'IXP2_T_943049'
  );
};

export const isFrictionlessPhase2SubscriptionsEnabled = (store: IXPSelectorState) => {
  return (
    !!store.featureFlags?.['cp-frictionless-subscriptions-phase2'] &&
    !!store?.ixp?.['FrictionlessRPPhase2'] &&
    !!store?.ixp?.['FrictionlessRPPhase2']?.isRunning &&
    store?.ixp?.['FrictionlessRPPhase2']?.treatmentKey === 'IXP2_T_1011450'
  );
};
/**
 * Check if batch payments feature is enabled
 * Requires:
 * 1. Feature flag enabled (cp-batch-payments for US, cp-batch-payments-canada for CA)
 * 2. Region matches flag (US for cp-batch-payments, CA for cp-batch-payments-canada)
 * 3. At least 1 invoice from API (+ default invoice = 2 total minimum)
 * 4. Not a recurring invoice (including forced autopay)
 * 5. Not a MAIP invoice (ACH convenience fee or CC surcharge enabled)
 */
export const isBatchPaymentsEnabled = (store: IXPSelectorState) => {
  const isBatchPaymentsFeatureFlagEnabled = store?.featureFlags?.['cp-batch-payments'];
  const isBatchPaymentsCanadaFeatureFlagEnabled = store?.featureFlags?.['cp-batch-payments-canada'];
  if (!isBatchPaymentsFeatureFlagEnabled && !isBatchPaymentsCanadaFeatureFlagEnabled) {
    return false;
  }

  const region = (store as RootState)?.companyInfo?.region;
  const isBatchPaymentsEnabledInRegion =
    (isBatchPaymentsCanadaFeatureFlagEnabled && region === COUNTRY.CA) ||
    (isBatchPaymentsFeatureFlagEnabled && region === COUNTRY.US);
  if (!isBatchPaymentsEnabledInRegion) {
    return false;
  }

  // Check if we have batch data first
  const batchData = (store as RootState)?.insight?.batchUnpaidInvoices;
  const unpaidInvoicesLength = batchData?.unpaidInvoices?.length;
  const hasEnoughInvoices = typeof unpaidInvoicesLength === 'number' && unpaidInvoicesLength >= 1;

  if (!hasEnoughInvoices) {
    return false;
  }

  // Check if this is a recurring or forced autopay invoice - batch payments not allowed
  // If sale doesn't exist, we can't verify it's safe, so return false to be defensive
  const sale = (store as RootState)?.sale;
  if (!sale) {
    return false;
  }

  // Exclude recurring invoices
  const hasRecurringInfo = !!sale?.recurringInfo;
  if (hasRecurringInfo) {
    return false;
  }

  // Exclude forced autopay invoices (extra check for clarity)
  const isForcedAutopay = isForcedAutopayRecurringSelector(store);
  if (isForcedAutopay) {
    return false;
  }

  // Exclude MAIP invoices - batch payments not allowed when MAIP is enabled
  // MAIP can be either ACH convenience fee or CC surcharge
  const isMaipEnabled =
    saleSelectors.achOnlineConvenienceFeeEnabledSelector(sale) ||
    saleSelectors.surchargeExperimentEnabledSelector(sale);
  if (isMaipEnabled) {
    return false;
  }

  return true;
};

/**
 * Check if user is in the Payment Retry experiment (either treatment group)
 * This experiment controls whether to show enhanced error messages after payment failure
 *
 * Treatment-1-positive (IXP2_T_1177521): Show new banner with actionable links (edit amount, try another method)
 * Treatment-2-negative (IXP2_T_1178708): Show banner with auto-triggered edit mode UI
 * Control (IXP2_T_1177520) / Not bucketed: Show generic decline message
 * https://experimentation.app.intuit.com/app/ixp-ui/experiments/createv2/301996?tab=setup
 */
export const isPaymentRetryExperimentTreatment = (store: IXPSelectorState): boolean => {
  try {
    const experimentKey = 'PaymentRetryExperiment';
    const treatmentKeyA = 'IXP2_T_1177521';
    const treatmentKeyB = 'IXP2_T_1178708';
    if (!store?.ixp?.[experimentKey]) {
      return false;
    }
    const { isRunning, treatmentKey } = store.ixp[experimentKey];
    // User is in treatment if experiment is running and they have either treatment key
    return isRunning === true && (treatmentKey === treatmentKeyA || treatmentKey === treatmentKeyB);
  } catch (error) {
    logToSplunk({ error });
    return false;
  }
};
/**
 * Check if user is in Treatment-A (IXP2_T_1177521)
 * Treatment-A: Show banner with actionable "Change payment amount" link
 * Check feature flag: https://featureflags-e2e.app.intuit.com/app/ixp-ui/feature-flags/e2e/SBSEG-CP-credit-floor-pm/version/latest
 */
export const isPaymentRetryExperimentTreatmentA = (store: IXPSelectorState): boolean => {
  try {
    const experimentKey = 'PaymentRetryExperiment';
    const treatmentKeyA = 'IXP2_T_1177521';
    if (!store?.ixp?.[experimentKey]) {
      return false;
    }
    const { isRunning, treatmentKey } = store.ixp[experimentKey];
    return (
      isRunning === true &&
      treatmentKey === treatmentKeyA &&
      !!store?.featureFlags?.['SBSEG-CP-credit-floor-pm']
    );
  } catch (error) {
    logToSplunk({ error });
    return false;
  }
};

/**
 * Check if user is in Treatment-B (IXP2_T_1178708)
 * Treatment-B: Show banner without link, auto-trigger edit mode with wrapped UI
 * Check feature flag: https://featureflags-e2e.app.intuit.com/app/ixp-ui/feature-flags/e2e/SBSEG-CP-partial-payment-workflows/version/latest
 */
export const isPaymentRetryExperimentTreatmentB = (store: IXPSelectorState): boolean => {
  try {
    const experimentKey = 'PaymentRetryExperiment';
    const treatmentKeyB = 'IXP2_T_1178708';
    if (!store?.ixp?.[experimentKey]) {
      return false;
    }
    const { isRunning, treatmentKey } = store.ixp[experimentKey];
    return (
      isRunning === true &&
      treatmentKey === treatmentKeyB &&
      !!store?.featureFlags?.['SBSEG-CP-partial-payment-workflows']
    );
  } catch (error) {
    logToSplunk({ error });
    return false;
  }
};

export type TreatmentBCreditFloorSelectorState = Pick<
  RootState,
  'ixp' | 'featureFlags' | 'payment'
>;

/**
 * Check if user is in TreatmentB credit floor scenario
 * This selector combines the treatment check with the credit floor decline check
 *
 * This validates:
 * 1. User is in TreatmentB
 * 2. creditFloorDeclineAmount > 0
 * 3. Decline reason code is SMALLER_PAYMENT (lowercase or uppercase)
 *
 * @param store - Redux store with ixp, featureFlags, and payment state
 * @returns true if user is in TreatmentB AND has a credit floor decline with proper reason code
 */
export const isTreatmentBCreditFloor = (store: TreatmentBCreditFloorSelectorState): boolean => {
  const isInTreatmentB = isPaymentRetryExperimentTreatmentB(store);
  const creditFloorDeclineAmount = store.payment?.creditFloorDeclineAmount;
  const paymentError = store.payment?.paymentError;

  // Check for specific credit floor reason codes (supports legacy lowercase and new uppercase)
  const hasReasonCode =
    paymentError?.reasonCode === PAYMENT_DECLINE_REASON_CODES.SMALLER_PAYMENT ||
    paymentError?.reasonCode === PAYMENT_DECLINE_REASON_CODES.SMALLER_PAYMENT_V2;

  return (
    !!isInTreatmentB &&
    typeof creditFloorDeclineAmount === 'number' &&
    creditFloorDeclineAmount > 0 &&
    hasReasonCode
  );
};

/**
 * Check if batch payments Card-on-File feature is enabled
 * 1. Batch payments enabled (via isBatchPaymentsEnabled selector)
 * 2. Feature flag 'cp-batch-payment-show-cof' enabled
 * 3. User in treatment group of IXP experiment 508903 (CardOnFileSCS)
 *
 * @param store - Redux store state
 * @returns true if batch COF should be shown, false otherwise
 */
export const isBatchCOFEnabled = (store: IXPSelectorState): boolean => {
  try {
    const batchEnabled = isBatchPaymentsEnabled(store);
    if (!batchEnabled) {
      return false;
    }

    const hasFeatureFlag = !!store.featureFlags?.['cp-batch-payment-show-cof'];
    if (!hasFeatureFlag) {
      return false;
    }

    const experimentKey = 'CardOnFileSCS';
    const cofExperiment = store.ixp?.[experimentKey];

    if (!cofExperiment) {
      return false;
    }

    const { isRunning, control } = cofExperiment;

    if (!isRunning || control === true) {
      return false;
    }

    return true;
  } catch (error) {
    logToSplunk({ error });
    return false;
  }
};

/**
 * Check if user is in NameValidationExperiment treatment.
 * Reads from store.ixp.NameValidationExperiment populated at page load.
 */
export const isInNameValidationExperiment = (store: IXPSelectorState): boolean => {
  try {
    const experimentKey = 'NameValidationExperiment';
    if (!store?.ixp?.[experimentKey]) {
      return false;
    }
    const { isRunning, control } = store.ixp[experimentKey];
    return isRunning === true && control === false;
  } catch (error) {
    logToSplunk({ error });
    return false;
  }
};

/**
 * Check if user is in AuthRateIncreaseHoldOut treatment.
 * Reads from store.ixp.AuthRateIncreaseHoldOut populated at page load.
 */
export const isAuthRateIncreaseHoldOut = (store: IXPSelectorState): boolean => {
  try {
    const experimentKey = 'AuthRateIncreaseHoldOut';
    if (!store?.ixp?.[experimentKey]) {
      return false;
    }
    const { isRunning, control } = store.ixp[experimentKey];
    return isRunning === true && control === false;
  } catch (error) {
    logToSplunk({ error });
    return false;
  }
};

/**
 * Check if user is in BillingAddressExperiment treatment.
 * Reads from store.ixp.BillingAddressExperiment populated at page load.
 */
export const isInBillingAddressExperiment = (store: IXPSelectorState): boolean => {
  try {
    const experimentKey = 'BillingAddressExperiment';
    if (!store?.ixp?.[experimentKey]) {
      return false;
    }
    const { isRunning, control } = store.ixp[experimentKey];
    return isRunning === true && control === false;
  } catch (error) {
    logToSplunk({ error });
    return false;
  }
};

/**
 * Check if user is in FcToGuestAttr treatment.
 * Reads from store.ixp.FcToGuestAttr populated at page load.
 */
export const isInFcToGuestAttrExperiment = (store: IXPSelectorState): boolean => {
  try {
    const experimentKey = 'FcToGuestAttr';
    if (!store?.ixp?.[experimentKey]) {
      return false;
    }
    const { isRunning, control } = store.ixp[experimentKey];
    return isRunning === true && control === false;
  } catch (error) {
    logToSplunk({ error });
    return false;
  }
};

/**
 * Check if user is in EnhancedDetailPaymentGw treatment.
 * Reads from store.ixp.EnhancedDetailPaymentGw populated at page load.
 */
export const isInEnhancedDetailPaymentGwExperiment = (store: IXPSelectorState): boolean => {
  try {
    const experimentKey = 'EnhancedDetailPaymentGw';
    if (!store?.ixp?.[experimentKey]) {
      return false;
    }
    const { isRunning, control } = store.ixp[experimentKey];
    return isRunning === true && control === false;
  } catch (error) {
    logToSplunk({ error });
    return false;
  }
};

/**
 * Returns true if the user should see the guest attributes collection flow.
 * True when in either FcToGuestAttr or EnhancedDetailPaymentGw treatment.
 */
export const shouldCollectGuestAttributes = (store: IXPSelectorState): boolean => {
  return isInFcToGuestAttrExperiment(store) || isInEnhancedDetailPaymentGwExperiment(store);
};

/**
 * Check if user is in EnhancementHoldOutExperiment treatment (holdout = control).
 * Reads from store.ixp.EnhancementHoldOutExperiment populated at page load.
 */
export const isInEnhancementsHoldoutExperiment = (store: IXPSelectorState): boolean => {
  try {
    const experimentKey = 'EnhancementHoldOutExperiment';
    if (!store?.ixp?.[experimentKey]) {
      return false;
    }
    const { isRunning, control } = store.ixp[experimentKey];
    return isRunning === true && control === false;
  } catch (error) {
    logToSplunk({ error });
    return false;
  }
};
